<template>
  <div class="pb-3">
    <va-card :title="$t(`app-features.cards.${titleCard}`)">
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onSubmit)">
          <div class="row">
            <div class="flex md6">
              <ValidationProvider rules="required" name="Chave" v-slot="{ errors }">
                <va-input
                  v-model="item.feature_key"
                  :disabled="updateId !== null"
                  :label="$t('app-features.forms.keyLabel')"
                  :placeholder="$t('app-features.forms.keyPlaceholder')"
                  :success="errors.length == 0 && item.feature_key.length > 0"
                  :error="errors.length > 0"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </div>
            <div class="flex md6">
              <ValidationProvider rules="required" name="Nome" v-slot="{ errors }">
                <va-input
                  v-model="item.name"
                  :label="$t('app-features.forms.nameLabel')"
                  :placeholder="$t('app-features.forms.namePlaceholder')"
                  :success="errors.length == 0 && item.name.length > 0"
                  :error="errors.length > 0"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </div>
          </div>
          <div class="row">
            <div class="flex md6">
              <ValidationProvider :rules="versionRule" name="Ocultar a partir da versão (iOS)" v-slot="{ errors }">
                <va-input
                  v-model="item.max_version_ios"
                  :label="$t('app-features.forms.maxVersionIosLabel')"
                  :placeholder="$t('app-features.forms.versionPlaceholder')"
                  :error="errors.length > 0"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </div>
            <div class="flex md6">
              <ValidationProvider :rules="versionRule" name="Ocultar a partir da versão (Android)" v-slot="{ errors }">
                <va-input
                  v-model="item.max_version_android"
                  :label="$t('app-features.forms.maxVersionAndroidLabel')"
                  :placeholder="$t('app-features.forms.versionPlaceholder')"
                  :error="errors.length > 0"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </div>
          </div>
          <div class="row">
            <div class="flex md12">
              <span>{{ $t('app-features.forms.activeLabel') }}</span>
              <va-toggle
                class="mb-3"
                v-model="item.is_active"
                small
                :label="item.is_active ? 'Sim' : 'Não'"
              />
              <va-input
                v-model="item.description"
                type="textarea"
                :label="$t('app-features.forms.descriptionLabel')"
                :placeholder="$t('app-features.forms.descriptionPlaceholder')"
              />
              <va-alert color="info" class="mt-3">
                {{ $t('app-features.forms.help') }}
              </va-alert>
              <div class="d-flex mt-3">
                <va-button
                  type="submit"
                  :disabled="buttonSubmitDisabled"
                  color="success"
                  class="my-0">
                  {{ textSubmit }}
                </va-button>
                <va-button
                  type="button"
                  class="my-0"
                  color="danger"
                  @click="cancel">
                  {{ $t("forms.cancel") }}
                </va-button>
              </div>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </va-card>
  </div>
</template>

<script>
import gql from 'graphql-tag'

const emptyItem = () => ({
  feature_key: '',
  name: '',
  is_active: true,
  max_version_ios: '',
  max_version_android: '',
  description: '',
})

export default {
  name: 'AppFeaturesAddUpdate',
  props: {
    page: {
      type: Number,
      required: true,
    },
    titleCard: {
      type: String,
      required: true,
    },
    updateId: {
      required: true,
    },
  },
  data () {
    return {
      item: emptyItem(),
      buttonSubmitDisabled: false,
      textSubmit: this.$t('forms.submit'),
    }
  },
  computed: {
    // Mesma regra do CHECK no banco: "1.2.0", nunca "1.2.0+42".
    versionRule () {
      return { regex: /^[0-9]+(\.[0-9]+)*$/ }
    },
  },
  mounted () {
    this.item = emptyItem()
    if (this.updateId !== null) {
      this.item_get(this.updateId)
    }
  },
  watch: {
    updateId (value) {
      if (this.updateId !== null) {
        this.item_get(this.updateId)
      } else {
        this.item = emptyItem()
      }
    },
  },
  methods: {
    cancel () {
      this.$emit('showAdd', false)
    },
    item_get (id) {
      this.$apollo.addSmartQuery('item', {
        query: gql`
        query MyQuery($id: Int!) {
          item: app_features_by_pk(id: $id) {
            feature_key
            name
            is_active
            max_version_ios
            max_version_android
            description
          }
        }
        `,
        variables: {
          id,
        },
        // Sem isso o Apollo devolve o registro em cache da abertura anterior e
        // o form reenvia o valor antigo ao salvar de novo.
        fetchPolicy: 'network-only',
        manual: true,
        result ({ data, loading }) {
          if (!loading) {
            this.item = Object.assign(emptyItem(), data.item, {
              max_version_ios: data.item.max_version_ios || '',
              max_version_android: data.item.max_version_android || '',
              description: data.item.description || '',
            })
          }
        },
      })
    },
    onSubmit () {
      this.buttonSubmitDisabled = true
      this.textSubmit = this.$t('forms.submitting')
      // Campo vazio precisa virar NULL: no banco, NULL significa liberado.
      const variables = {
        name: this.item.name,
        is_active: this.item.is_active,
        max_version_ios: this.item.max_version_ios || null,
        max_version_android: this.item.max_version_android || null,
        description: this.item.description || null,
      }
      this.$apollo
        .mutate({
          mutation: (
            this.updateId !== null ? gql`
              mutation MyMutation(
                $id: Int!, $name: String!, $is_active: Boolean!,
                $max_version_ios: String, $max_version_android: String,
                $description: String
              ) {
                update_app_features_by_pk(
                  pk_columns: {id: $id},
                  _set: {
                    name: $name,
                    is_active: $is_active,
                    max_version_ios: $max_version_ios,
                    max_version_android: $max_version_android,
                    description: $description
                  }
                ) {
                  id
                }
              }
              ` : gql`
              mutation MyMutation(
                $feature_key: String!, $name: String!, $is_active: Boolean!,
                $max_version_ios: String, $max_version_android: String,
                $description: String
              ) {
                insert_app_features(objects: {
                  feature_key: $feature_key,
                  name: $name,
                  is_active: $is_active,
                  max_version_ios: $max_version_ios,
                  max_version_android: $max_version_android,
                  description: $description
                }) {
                  affected_rows
                }
              }
              `),
          variables: this.updateId !== null
            ? Object.assign({ id: this.updateId }, variables)
            : Object.assign({ feature_key: this.item.feature_key }, variables),
        })
        .then(data => {
          this.buttonSubmitDisabled = false
          this.textSubmit = this.$t('forms.submit')
          this.item = emptyItem()
          this.$emit('showAdd', false)
          this.$emit('showMore', this.page)
          this.showToast(
            this.$t(
              this.updateId != null ? 'toasts.successUpdate' : 'toasts.successCreate',
            ),
            {
              icon: 'fa-check',
              position: 'top-right',
              duration: 1000,
              fullWidth: false,
            },
          )
        })
        .catch(_ => {
          this.buttonSubmitDisabled = false
          this.textSubmit = this.$t('forms.submit')
          this.$emit('showAdd', false)
          this.showToast(
            this.$t(
              this.updateId != null ? 'toasts.errorUpdate' : 'toasts.errorCreate',
            ),
            {
              icon: 'fa-check',
              position: 'top-right',
              duration: 1000,
              fullWidth: false,
            },
          )
        })
    },
  },
}
</script>

<style>
</style>
